/**
 * Shared-hub realtime channel (Supabase Realtime), with per-instance CAPACITY + sharding.
 *
 * The hub runs the REAL game engine in a peaceful "hub mode": each client broadcasts its full
 * ship state {x,y,heading,tier,vx,vy} and receives everyone else's, so players see each other's
 * actual ships fly around. No server authority (cosmetic). Presence drives the per-instance count
 * + clean leave; positions come over broadcast so idle players cost almost nothing.
 *
 * "Server size": each instance holds up to HUB_CAPACITY pilots. Auto-join probes hub-1, hub-2, …
 * (like MMO channels); the player can also explicitly pick a server (opts.instance). This bounds
 * the O(N) broadcast fan-out per instance — free-tier friendly. A Cloudflare Durable Object takes
 * over when this needs real scale.
 */
import { supabase } from "./supabase";
import type { Account } from "./auth";

export interface HubMove {
  id: string;
  username: string;
  x: number;
  y: number;
  h: number; // heading (radians)
  tier: number; // hull tier 0..7 → which ship sprite
  vx: number; // velocity (drives remote thrusters)
  vy: number;
  seg: number; // worm body-segment count (tiers 6/7 render as a worm chain; 0 otherwise)
}

export interface HubCallbacks {
  onMove: (m: HubMove) => void;
  onLeave: (id: string) => void;
  onCount: (n: number) => void;
  onInstance?: (n: number) => void;
}

export interface HubHandle {
  send: (x: number, y: number, h: number, tier: number, vx: number, vy: number, seg: number) => void;
  leave: () => void;
}

/** Max pilots per hub instance — the "server size". Tune freely. Overflow → next instance. */
export const HUB_CAPACITY = 15;
const MAX_INSTANCES = 50;

/** Hull tier from a serialized save's upgrade list — mirrors GameState.hullTier. */
export function tierFromUpgrades(upg: string[]): number {
  const s = new Set(upg);
  if (s.has("mkviii")) return 7;
  if (s.has("mkvii")) return 6;
  if (s.has("mkvi_bts") || s.has("mkvi_swl")) return 5;
  if (s.has("mkv_bts") || s.has("mkv_swl")) return 4;
  if (s.has("mk4")) return 3;
  if (s.has("mk3")) return 2;
  if (s.has("mk2")) return 1;
  return 0;
}

export function joinHub(
  account: Account,
  cb: HubCallbacks,
  opts?: { instance?: number },
): HubHandle {
  if (!supabase) {
    cb.onCount(1);
    cb.onInstance?.(opts?.instance ?? 1);
    return { send: () => {}, leave: () => {} };
  }
  const client = supabase;
  type Ch = ReturnType<typeof client.channel>;
  let current: Ch | null = null;
  let active: Ch | null = null;
  let leaving = false;

  const validMove = (p?: HubMove): p is HubMove =>
    !!p &&
    typeof p.id === "string" &&
    p.id !== account.id &&
    typeof p.username === "string" &&
    Number.isFinite(p.x) &&
    Number.isFinite(p.y) &&
    Number.isFinite(p.h) &&
    Number.isFinite(p.tier) &&
    Number.isFinite(p.vx) &&
    Number.isFinite(p.vy) &&
    Number.isFinite(p.seg);

  const attach = (instance: number, forced: boolean): void => {
    if (leaving) return;
    const ch = client.channel(`hub-${instance}`, {
      config: { presence: { key: account.id }, broadcast: { self: false } },
    });
    current = ch;
    let committed = false;
    const recount = (): void => cb.onCount(Object.keys(ch.presenceState()).length || 1);

    const decide = (): void => {
      if (committed || leaving) return;
      const state = ch.presenceState();
      if (!state[account.id]) return; // wait until my presence lands (timeout below covers stalls)
      const n = Object.keys(state).length;
      if (!forced && n > HUB_CAPACITY && instance < MAX_INSTANCES) {
        committed = true; // this server is full → hop to the next (unless the player forced it)
        void client.removeChannel(ch);
        attach(instance + 1, false);
        return;
      }
      committed = true;
      active = ch;
      cb.onInstance?.(instance);
      recount();
    };

    ch.on("presence", { event: "sync" }, () => (committed ? recount() : decide()));
    ch.on("presence", { event: "leave" }, (p: { key?: string }) => {
      if (!committed) return;
      if (p.key) cb.onLeave(p.key);
      recount();
    });
    ch.on("broadcast", { event: "move" }, (m: { payload?: HubMove }) => {
      if (committed && validMove(m.payload)) cb.onMove(m.payload);
    });
    ch.subscribe((status) => {
      if (status === "SUBSCRIBED") void ch.track({ username: account.username });
    });

    setTimeout(() => {
      if (!committed && !leaving && current === ch) {
        committed = true;
        active = ch;
        cb.onInstance?.(instance);
        recount();
      }
    }, 2500);
  };

  attach(opts?.instance ?? 1, typeof opts?.instance === "number");

  return {
    send: (x, y, h, tier, vx, vy, seg) => {
      if (active) {
        void active.send({
          type: "broadcast",
          event: "move",
          payload: { id: account.id, username: account.username, x, y, h, tier, vx, vy, seg } satisfies HubMove,
        });
      }
    },
    leave: () => {
      leaving = true;
      if (current) void client.removeChannel(current);
    },
  };
}
